"use client";

import { FlexRowAlignCenter } from "@/components/Flex";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { themeColors } from "@/utils/themeColors";
import { STUDENT_PRICE_ID } from "@knowt/syncing/hooks/user/subscriptionConstants";
import { getPricingCardProps } from "@knowt/syncing/hooks/user/subscriptions";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { SxProps } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { PricingPropsUI } from "../../utils/constants";
import PricingCard, { PromoBadge } from "./PricingCard";

export default function PlanOptions({
    pricingOptions,
    customOnClick,
    containerStyle,
    princingCardSx,
    isSimpleClick = false,
    isForBulkPricing = false,
    benefitCheckStyle,
}: {
    pricingOptions: PricingPropsUI[];
    customOnClick?: (plan) => void;
    containerStyle?: React.CSSProperties;
    princingCardSx?: SxProps;
    isSimpleClick?: boolean;
    isForBulkPricing?: boolean;
    benefitCheckStyle?: React.CSSProperties;
}) {
    const { user, organization } = useCurrentUser();
    const { smDown } = useBreakPoints();

    // NOTE: this is to make sure `isUnidays` does not cause client `Hydration failed` error
    const [startCheckIsUnidays, setStartCheckIsUnidays] = useState(false);
    useEffect(() => {
        setTimeout(() => {
            setStartCheckIsUnidays(true);
        }, 100);
    }, []);

    return (
        <FlexRowAlignCenter
            style={{ gap: "3rem", flexWrap: "wrap", justifyContent: "center", marginTop: "8rem", ...containerStyle }}>
            {pricingOptions.map((pricingOption, index) => {
                const { buttonText, isCurrentPlan } = getPricingCardProps(pricingOption, user, organization);

                const isUnidays =
                    startCheckIsUnidays &&
                    typeof window !== "undefined" &&
                    window.location.pathname.includes("unidays") &&
                    (pricingOption.priceId === STUDENT_PRICE_ID.LIMITLESS_MONTHLY_10 ||
                        pricingOption.priceId === STUDENT_PRICE_ID.PRO_YEARLY_36);

                if (!pricingOption) return null;

                const { mostPopular, promo, type } = pricingOption;

                const badgeText = isForBulkPricing
                    ? null
                    : mostPopular && promo
                      ? `Most popular & ${promo}% off for a limited time`
                      : mostPopular
                        ? "Most popular"
                        : promo
                          ? `${promo}% off for a limited time`
                          : null;

                const isLimitless = type === "Student Ultra" || type === "Teacher Ultra";

                return (
                    <PricingCard
                        key={pricingOption.interval + pricingOption.type + index}
                        sx={{
                            scale: "1",
                            width: smDown ? "100%" : "35rem",
                            ...princingCardSx,
                        }}
                        isCurrentPlan={Boolean(isCurrentPlan)}
                        buttonText={pricingOption.nbrLicenses ? "Change Plan" : buttonText}
                        renderTopBadge={
                            isUnidays ? (
                                <PromoBadge
                                    text={
                                        <span style={{ color: "#30333d", fontSize: "1.25rem" }}>
                                            {`Additional 15% off with UNiDAYS!`}
                                        </span>
                                    }
                                    backgroundColor="#ffcd1f"
                                />
                            ) : badgeText ? (
                                <PromoBadge
                                    text={
                                        <span
                                            style={{
                                                color: isLimitless ? "#30333d" : themeColors.pureWhite,
                                                fontSize: "1.25rem",
                                            }}>
                                            {badgeText}
                                        </span>
                                    }
                                    backgroundColor={isLimitless ? "#ffcd1f" : "#092522"}
                                />
                            ) : null
                        }
                        isSimpleClick={isSimpleClick}
                        {...pricingOption}
                        {...(customOnClick &&
                            !pricingOption.needContactUs && { onClick: () => customOnClick(pricingOption) })}
                        {...(isUnidays && {
                            buttonText: "claim UNiDAYS offer",
                            buttonBackground: themeColors.primary,
                            price: `$${(Number.parseFloat(pricingOption.price.slice(1)) * 0.85).toFixed(2)}`,
                            oldPrice: pricingOption.oldPrice || pricingOption.pricePerMonth || pricingOption.price,
                        })}
                        type={pricingOption.type}
                        btnSx={
                            !isLimitless
                                ? {
                                      "&:hover": {
                                          backgroundColor: themeColors.neutralBlack,
                                          color: themeColors.neutralWhite,
                                      },
                                  }
                                : {}
                        }
                        specialBenefit={pricingOption.specialBenefit}
                        isForBulkPricing={isForBulkPricing}
                        benefitCheckStyle={benefitCheckStyle}
                    />
                );
            })}
        </FlexRowAlignCenter>
    );
}
